import { newId, touchContentUpdatedAt, validateLayer } from "../model/surfDeck.js";
import { clampNormRect, clampNormRectEditor } from "./coords.js";
import { normalizeLayerStyle } from "./layerStyle.js";
import { emptyTableData } from "./tableLayer.js";
import { emptyChartData } from "../chart/chartLayer.js";
import { emptyGraphicData } from "../graphic/graphicLayer.js";
export function sortedLayers(slide) {
    return [...slide.layers].sort((a, b) => a.z - b.z || a.id.localeCompare(b.id));
}
export function nextLayerZ(slide) {
    if (!slide.layers.length)
        return 1;
    return Math.max(...slide.layers.map((l) => l.z)) + 1;
}
export function setSlideLayers(deck, slideId, layers) {
    return updateSlideInDeck(deck, slideId, (slide) => ({ ...slide, layers }));
}
export function updateSlideInDeck(deck, slideId, updater) {
    return touchContentUpdatedAt({
        ...deck,
        slides: deck.slides.map((s) => (s.id === slideId ? updater(s) : s)),
    });
}
function normalizeSlideBackgroundHex(hex) {
    if (!hex)
        return undefined;
    const raw = hex.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(raw))
        return raw.toUpperCase();
    if (/^#[0-9A-Fa-f]{3}$/.test(raw)) {
        const h = raw.slice(1);
        return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toUpperCase();
    }
    return undefined;
}
/** Per-slide solid background color (palette or picker). Merge-preserving. */
export function setSlideBackgroundColor(deck, slideId, hex) {
    const solidColor = normalizeSlideBackgroundHex(hex);
    return updateSlideInDeck(deck, slideId, (s) => ({
        ...s,
        background: { ...s.background, solidColor },
    }));
}
export function updateLayerInDeck(deck, slideId, layerId, patch) {
    return updateSlideInDeck(deck, slideId, (slide) => ({
        ...slide,
        layers: slide.layers.map((layer) => {
            if (layer.id !== layerId)
                return layer;
            const merged = { ...layer, ...patch };
            if (patch.x !== undefined || patch.y !== undefined || patch.w !== undefined || patch.h !== undefined) {
                const clamped = clampNormRectEditor(merged);
                merged.x = clamped.x;
                merged.y = clamped.y;
                merged.w = clamped.w;
                merged.h = clamped.h;
            }
            const next = normalizeLayerStyle(merged);
            const err = validateLayer(next);
            if (err)
                throw new Error(err);
            return next;
        }),
    }));
}
export function deleteLayerInDeck(deck, slideId, layerId) {
    return updateSlideInDeck(deck, slideId, (slide) => ({
        ...slide,
        layers: slide.layers.filter((l) => l.id !== layerId),
    }));
}
export function duplicateLayerInDeck(deck, slideId, layerId) {
    return updateSlideInDeck(deck, slideId, (slide) => {
        const source = slide.layers.find((l) => l.id === layerId);
        if (!source)
            return slide;
        const bumped = clampNormRect({
            x: source.x + 0.02,
            y: source.y + 0.02,
            w: source.w,
            h: source.h,
        });
        const copy = {
            ...structuredClone(source),
            id: newId(),
            x: bumped.x,
            y: bumped.y,
            z: nextLayerZ(slide),
        };
        return { ...slide, layers: [...slide.layers, copy] };
    });
}
export function changeLayerZOrder(deck, slideId, layerId, mode) {
    return updateSlideInDeck(deck, slideId, (slide) => {
        const ordered = sortedLayers(slide);
        const index = ordered.findIndex((l) => l.id === layerId);
        if (index < 0)
            return slide;
        const reordered = [...ordered];
        const [item] = reordered.splice(index, 1);
        if (mode === "front")
            reordered.push(item);
        else if (mode === "back")
            reordered.unshift(item);
        else if (mode === "forward" && index < ordered.length - 1) {
            reordered.splice(index + 1, 0, item);
        }
        else if (mode === "backward" && index > 0) {
            reordered.splice(index - 1, 0, item);
        }
        else {
            reordered.splice(index, 0, item);
        }
        const layers = reordered.map((layer, z) => ({ ...layer, z: z + 1 }));
        return { ...slide, layers };
    });
}
export function addLayerToSlide(deck, slideId, type, defaults) {
    return updateSlideInDeck(deck, slideId, (slide) => {
        const z = nextLayerZ(slide);
        const base = {
            id: newId(),
            type,
            x: 0.1,
            y: 0.1,
            w: type === "table" ? 0.5 : type === "chart" || type === "graphic" ? 0.45 : 0.35,
            h: type === "text" ? 0.12 : type === "table" || type === "chart" || type === "graphic" ? 0.35 : 0.2,
            z,
            rotation: 0,
            content: type === "text"
                ? "Nuovo testo"
                : type === "shape"
                    ? ""
                    : type === "table" || type === "chart"
                        ? ""
                        : type === "graphic"
                            ? ""
                            : "Immagine",
            textRole: type === "text" ? "body" : undefined,
            style: type === "table"
                ? { table: emptyTableData() }
                : type === "chart"
                    ? { chart: emptyChartData() }
                    : type === "graphic"
                        ? { graphic: emptyGraphicData() }
                        : {},
            ...defaults,
        };
        const err = validateLayer(base);
        if (err)
            throw new Error(err);
        return { ...slide, layers: [...slide.layers, base] };
    });
}
export function toggleLegibilityStrategy(deck, strategy) {
    return touchContentUpdatedAt({
        ...deck,
        theme: {
            ...deck.theme,
            legibility: { ...deck.theme.legibility, strategy },
        },
    });
}
/** Assign an uploaded S3 key to an image layer and clear AI-baked overlay state. */
export function setImageLayerAsset(deck, slideId, layerId, s3Key, options) {
    return updateSlideInDeck(deck, slideId, (slide) => ({
        ...slide,
        layers: slide.layers.map((layer) => {
            if (layer.id !== layerId)
                return layer;
            const { textRender, bakedText, imageSubject, cutout, crop, ...restStyle } = layer.style ?? {};
            const natW = options?.naturalWidth;
            const natH = options?.naturalHeight;
            const h = natW && natH && natW > 0 ? layer.w * (natH / natW) : layer.h;
            const next = {
                ...layer,
                content: s3Key,
                h,
                style: restStyle,
            };
            const err = validateLayer(next);
            if (err)
                throw new Error(err);
            return normalizeLayerStyle(next);
        }),
    }));
}
